import { adjustedCategory } from "../config/productCategories";
import type { ProductListItem } from "../types/Product";
import { getLastChangeTimestamp } from "../components/ProductItem.utils";

const MIN_CATEGORY_RAIL_DEALS = 5;
// Same balance as the "Best deal" sort: percent off times the square root of
// dollars saved, so an 80%-off $40 item can beat a 55%-off fridge without a
// pile of $3 markdowns taking over.
const topDealScore = (product: ProductListItem, price: number, discount: number) =>
  discount * Math.sqrt(Math.max(getTopProductOriginalPrice(product) - price, 0));

const PREFERRED_TOP_DEAL_CATEGORIES = new Set([
  "electronics",
  "tools",
  "toys",
  "home",
  "kitchen",
  "baby-and-kids",
  "outdoors",
  "vacuum",
  "cleaning",
]);

/**
 * Top Deals is intentionally a high-intent discovery surface. Apparel, shoes,
 * beauty, food, and pharmacy can still be found in All Deals, but they should
 * never displace durable-goods finds in the hero or curated rails.
 */
export const isPreferredTopDealProduct = (product: ProductListItem) => {
  const normalized = adjustedCategory(product) || product.category?.trim().toLowerCase();
  return PREFERRED_TOP_DEAL_CATEGORIES.has(normalized);
};
export const getTopProductPrice = (p: ProductListItem) => {
  if (p.openBox && p.openBoxPrice?.length) return p.openBoxPrice[p.openBoxPrice.length - 1];
  const sp = p.storePrice || [];
  return sp.length ? sp[sp.length - 1] : 0;
};
export const getTopProductOriginalPrice = (p: ProductListItem) => Math.max(p.msrp || 0, p.onlinePrice || 0);
export const getTopProductDiscountPct = (p: ProductListItem) => {
  const o = getTopProductOriginalPrice(p);
  const c = getTopProductPrice(p);
  if (o <= 0 || c <= 0 || c >= o) return 0;
  return Math.round(((o - c) / o) * 100);
};
const getLastTs = (p: ProductListItem) =>
  (p as { lastUpdated?: number }).lastUpdated ||
  p.lastUpdates?.[p.lastUpdates.length - 1] ||
  p.firstInserted ||
  0;
const getLastChangedTs = (p: ProductListItem) =>
  (p as { lastChangedTime?: number; lastChanged?: number }).lastChangedTime ||
  (p as { lastChangedTime?: number; lastChanged?: number }).lastChanged ||
  getLastChangeTimestamp({
    priceHistory: p.openBox && p.openBoxPrice?.length ? p.openBoxPrice : p.storePrice,
    backroomHistory: p.backroom,
    salesFloorHistory: p.salesFloor,
    lastUpdates: p.lastUpdates,
  }) ||
  0;
export const isNew24h = (p: ProductListItem, now = Date.now()) =>
  Boolean(p.firstInserted) && (now - p.firstInserted) / (1000 * 60 * 60) <= 24;
export const isAtLowestPrice = (p: ProductListItem) => {
  const prices = p.storePrice;
  if (!prices || prices.length < 3) return false;
  const current = prices[prices.length - 1];
  if (current <= 0) return false;
  const recentWindow = prices.slice(Math.max(0, prices.length - 4), prices.length - 1);
  if (!recentWindow.every((x) => x > current)) return false;
  for (let i = 0; i < prices.length - 1; i++) {
    if (prices[i] > 0 && prices[i] < current) return false;
  }
  return true;
};

interface Ranked {
  product: ProductListItem;
  discount: number;
  price: number;
  lastTs: number;
  lastChangedTs: number;
}

const diversifyByCategory = <T extends { adjustedCategory?: string }>(items: T[]): T[] => {
  const remaining = [...items];
  const out: T[] = [];
  let prev: string | undefined;
  while (remaining.length) {
    let idx = remaining.findIndex(
      (x) => adjustedCategory(x) !== (prev || "")
    );
    if (idx < 0) idx = 0;
    const [picked] = remaining.splice(idx, 1);
    out.push(picked);
    prev = adjustedCategory(picked);
  }
  return out;
};

const rankProducts = (products: ProductListItem[]): Ranked[] => {
  const map = new Map<string, Ranked>();
  for (const p of products) {
    const key = p.upc || p.sku || "";
    if (!key) continue;
    const ranked: Ranked = {
      product: p,
      discount: getTopProductDiscountPct(p),
      price: getTopProductPrice(p),
      lastTs: getLastTs(p),
      lastChangedTs: getLastChangedTs(p),
    };
    const existing = map.get(key);
    if (
      !existing ||
      ranked.discount > existing.discount ||
      (ranked.discount === existing.discount && ranked.lastTs > existing.lastTs)
    ) {
      map.set(key, ranked);
    }
  }
  return Array.from(map.values());
};

/**
 * The hero slot (featured[0]) must always be a deal first inserted today, so the
 * Top tab always leads with something new. The best-scoring new deal is moved to
 * the front; the rest keep their ranked, category-diversified order.
 */
const leadWithNewDeal = (
  ordered: ProductListItem[],
  candidates: Ranked[],
  score: (r: Ranked) => number,
  now: number,
): ProductListItem[] => {
  const lead = candidates
    .filter((r) => isNew24h(r.product, now))
    .sort((a, b) => score(b) - score(a) || b.discount - a.discount || (b.product.firstInserted || 0) - (a.product.firstInserted || 0))[0]?.product;
  if (!lead) return ordered;
  const leadId = lead.upc || lead.sku;
  return [lead, ...ordered.filter((p) => (p.upc || p.sku) !== leadId)];
};

export function buildTopProductsViewModel(
  products: ProductListItem[],
  hoursWindow = 24,
  now = Date.now(),
) {
  const ranked = rankProducts(products).filter((item) =>
    isPreferredTopDealProduct(item.product)
  );
  const cutoff = now - hoursWindow * 3600 * 1000;
  const recent = ranked.filter((r) => r.lastTs > cutoff && r.price > 0);
  const withDiscount = recent.filter((r) => r.discount > 0);
  const dealScore = (r: Ranked) =>
    topDealScore(r.product, r.price, r.discount);
  const changedWithinWindow = (r: Ranked) => r.lastChangedTs > cutoff;
  const byDiscount = leadWithNewDeal(
    diversifyByCategory(
      [...withDiscount]
        .sort(
          (a, b) =>
            Number(changedWithinWindow(b)) - Number(changedWithinWindow(a)) ||
            dealScore(b) - dealScore(a) ||
            b.discount - a.discount ||
            b.lastChangedTs - a.lastChangedTs
        )
        .map((r) => r.product)
    ),
    withDiscount,
    dealScore,
    now,
  );
  const justDroppedAll = diversifyByCategory(
    recent
      .filter((r) => isNew24h(r.product, now) && r.discount > 0)
      .sort(
        (a, b) =>
          dealScore(b) - dealScore(a) ||
          b.discount - a.discount ||
          (b.product.firstInserted || 0) - (a.product.firstInserted || 0)
      )
      .map((r) => r.product)
  );
  const newLowerPriceAll = diversifyByCategory(
    recent
      .filter((r) => isAtLowestPrice(r.product))
      .sort((a, b) => dealScore(b) - dealScore(a) || b.discount - a.discount)
      .map((r) => r.product)
  );
  const avgDiscount = withDiscount.length
    ? Math.round(withDiscount.reduce((s, r) => s + r.discount, 0) / withDiscount.length)
    : 0;

  const catMap = new Map<string, Ranked[]>();
  for (const r of withDiscount) {
    const cat = adjustedCategory(r.product);
    if (!cat) continue;
    const arr = catMap.get(cat);
    if (arr) arr.push(r);
    else catMap.set(cat, [r]);
  }
  const categoryRails = Array.from(catMap.entries())
    .map(([category, items]) => {
      const sorted = [...items].sort((a, b) => b.discount - a.discount);
      const avg = Math.round(sorted.reduce((s, p) => s + p.discount, 0) / sorted.length);
      return {
        category,
        count: sorted.length,
        avg,
        items: sorted.slice(0, 10).map((p) => p.product),
      };
    })
    .filter((c) => c.count >= MIN_CATEGORY_RAIL_DEALS)
    .sort((a, b) => b.count * b.avg - a.count * a.avg);

  const stats = {
    recentCount: recent.length,
    avgDiscount,
    featured: byDiscount.slice(0, 10),
    picks: byDiscount.slice(10, 22),
    justDropped: justDroppedAll.slice(0, 20),
    justDroppedCount: justDroppedAll.length,
    newLowerPrice: newLowerPriceAll.slice(0, 12),
    newLowerPriceCount: newLowerPriceAll.length,
    categoryRails,
  };

  const hasRecent = stats.recentCount > 0;
  const fallback = (() => {
    if (hasRecent) return null;
    const score = (r: Ranked) =>
      topDealScore(r.product, r.price, r.discount);
    const eligible = rankProducts(products)
      .filter((item) => isPreferredTopDealProduct(item.product))
      .filter((r) => r.price > 0);
    const ranked = leadWithNewDeal(
      diversifyByCategory(
        [...eligible]
          .sort((a, b) => score(b) - score(a) || b.discount - a.discount)
          .map((r) => r.product)
      ),
      eligible,
      score,
      now,
    );
    return {
      featured: ranked.slice(0, 10),
      picks: ranked.slice(10, 22),
    };
  })();

  const featured = hasRecent ? stats.featured : fallback?.featured || [];
  const picks = hasRecent ? stats.picks : fallback?.picks || [];

  // Count each displayed deal once, even when it appears in several sections.
  const displayed = [
    ...featured, ...picks, ...stats.justDropped, ...stats.newLowerPrice,
    ...stats.categoryRails.flatMap((rail) => rail.items),
  ];
  const count = new Set(displayed.map((product) => product.upc || product.sku)).size;
  return { stats, hasRecent, featured, picks, count, hoursWindow };
}

export type TopProductsViewModel = ReturnType<typeof buildTopProductsViewModel>;
